export const PYTHON_REALM_PROTOCOL = "kernel-python-realm";
export const PYTHON_REALM_PROTOCOL_VERSION = 1;

export const REALM_BOOTSTRAP_CAPABILITIES = Object.freeze([
    "classicBlobWorker",
]);

export const PYTHON_WORKER_CAPABILITIES = Object.freeze([
    "classicBlobWorker",
    "dynamicImport",
    "webAssembly",
    "pythonRuntime",
    "controlChannelLocked",
    "networkGlobalsRemoved",
    "verifiedWheelInjection",
]);

const CRYPTOGRAPHIC_ID_PATTERN = /^[a-f0-9]{32}$/;
const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "[::1]"]);

export function createCryptographicId(cryptoImpl = globalThis.crypto) {
    if (!cryptoImpl || typeof cryptoImpl.getRandomValues !== "function") {
        throw new Error("cryptographic randomness is unavailable");
    }

    const bytes = new Uint8Array(16);
    cryptoImpl.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function isCryptographicId(value) {
    return typeof value === "string" && CRYPTOGRAPHIC_ID_PATTERN.test(value);
}

export function requireExactWebOrigin(value, { allowLocalHttp = true } = {}) {
    if (typeof value !== "string" || !value) {
        throw new Error("origin must be a non-empty string");
    }

    let url;
    try {
        url = new URL(value);
    } catch {
        throw new Error("origin is not a valid URL");
    }

    if (
        url.origin !== value
        || url.username
        || url.password
        || url.hostname.includes("*")
    ) {
        throw new Error("origin must contain only scheme, host, and optional port");
    }

    const isLocalHostname = LOCAL_HOSTNAMES.has(url.hostname)
        || url.hostname.endsWith(".localhost");
    const isLocalHttp = allowLocalHttp
        && url.protocol === "http:"
        && isLocalHostname;
    if (url.protocol !== "https:" && !isLocalHttp) {
        throw new Error("origin must use HTTPS");
    }

    return url.origin;
}

export function createProtocolEnvelope(channelId, type, payload = {}) {
    if (!isCryptographicId(channelId)) {
        throw new Error("invalid realm channel id");
    }
    if (typeof type !== "string" || !type) {
        throw new Error("invalid realm message type");
    }

    return {
        ...payload,
        protocol: PYTHON_REALM_PROTOCOL,
        protocolVersion: PYTHON_REALM_PROTOCOL_VERSION,
        channelId,
        type,
    };
}

export function isProtocolEnvelope(value, channelId) {
    return Boolean(
        value
        && typeof value === "object"
        && value.protocol === PYTHON_REALM_PROTOCOL
        && value.protocolVersion === PYTHON_REALM_PROTOCOL_VERSION
        && value.channelId === channelId
        && typeof value.type === "string",
    );
}

export function missingCapabilities(reported, required) {
    if (!reported || typeof reported !== "object" || Array.isArray(reported)) {
        return [...required];
    }
    return required.filter((name) => reported[name] !== true);
}
